import { useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { IPageState, PageContext } from '../../contexts/PageContext';
import { CHARGE_TYPES, JOB_WORK_STEPS, PATHS, TRANSACTION_STATUS } from '../../utils/constant';
import Delivery from '../$Delivery';
import PhysicalOrder from '../$PhysicalOrder';
import Inbound from '../$Inbound';
import Billing from '../$Billing';
import Summary from '../$Summary';
import TransferPvcToPlant from '../$PvcToPlant';
import { formatDate, getFormattedCurrency, getThreePointNumber, getTwoPointNumber, objToArray } from '../../utils/helpers';
import SelectCustomer from '../$SelectCustomer';
import { TTransactionBasic } from '../../Models/Transactions';
import useService from '../../hooks/useService';
import { fetchAnnexure, getLotDetails, getTransaction as getTransactionService } from '../../services/Transactions'
import ConfirmPhysicalOrder from '../$ConfirmPhysicalOrder';
import HighlightsCardAndCustomerDetails from '../custom/HighlightsCardAndCustomerDetails';
import DownloadInvoice from '../$Summary/DownloadInvoice';
import { Button } from '@mui/material';
import { Description } from "@mui/icons-material";


export type TJobWorkSectionKeys = 'INBOUND' | 'PHYSICAL_ORDER' | 'PVC_TO_PLANT' | 'CONFIRM_PHYSICAL_ORDER' | 'DELIVERY' | 'BILLING' | 'SUMMARY' //Job Work breadCrum

const PAGE_INFO: IPageState = {
    title: 'Job Work',
    breadcrumbs: objToArray(JOB_WORK_STEPS).map(item => ({ path: item.key, label: item.value })),
    currentPage: JOB_WORK_STEPS.INBOUND,
    path: PATHS.JOB_WORK
}

type TGMSSummaryKeys = 
'customerId' 
| 'customerName' 
| 'pvcName' 
| 'transactionDate' 
| 'deliveryCharge'
| 'totalWBMGold'
| 'totalWAMGold'
| 'totalWAMTGold'
| 'settlementFineWeightGold'
| 'totalTaxValue'
| 'totalCharges'
| 'roundingRs'
| 'totalValue'
| 'totalMetalReturnNetWeightGold'

const SUMMARY_DETAILS: {id: TGMSSummaryKeys, label: any, value: any}[] = [
    {
        id: 'customerId',
        label:"Customer Id",
        value : ""
    },
    {
        id:"customerName",
        label:"Customer Name",
        value : ""
    },
    {
        id:"pvcName",
        label:"PVC Name",
        value : ""
    },
    {
        id:"transactionDate",
        label:"Transaction Date",
        value : ""
    },
    {
        id: 'deliveryCharge',
        label: "Delivery Charges",
        value : ''
    },
    // {
    //     id:"inboundMetal",
    //     label:"Inbound Metal",
    //     value : ""
    // },
    // {
    //     id:"totalPrice",
    //     label:"Total Price",
    //     value : ""
    // },
    {
        id:'totalWBMGold',
        label:"Total WBM",
        value : ''
    },
    {
        id:'totalWAMGold',
        label:"Total WAM",
        value : ''
    },
    {
        id:'totalWAMTGold',
        label:"Total WAMT",
        value : ''
    },
    {
        id:'settlementFineWeightGold',
        label:"Settlement Fine Wt. Gold",
        value : ''
    },
    {
        id:'totalMetalReturnNetWeightGold',
        label:"Total Metal Return Net Wt. Gold",
        value : ''
    },
    {
        id:'totalTaxValue',
        label:"Total Tax Value Rs.",
        value : ''
    },
    {
        id:'totalCharges',
        label:"Total Charges Rs.",
        value : ''
    },
    {
        id:'roundingRs',
        label:"Rounding Rs.",
        value : ''
    },
    {
        id:'totalValue',
        label:"Grand Total Rs.",
        value : ''
    },
]

const Container = styled.div`
    width: 100%;
`


const getPageInfo = (section: TJobWorkSectionKeys) => {
    const pageInfo = { ...PAGE_INFO, currentPage: section || 'INBOUND' }
    return pageInfo
}


const JobWork = () => {

    const { setPageState } = useContext(PageContext)
    const { execute, store } = useService()
    const { setTransactionInfo, physicalOrder, pvcCenterDetails, transactionInfo: { transactionType, globalTransactionNumber, status , updatedAt}, inboundDetails, resetOrder, setCapturedSteps, meltingLot } = store.transactionsStore
    const { user } = store.authStore
    const { customerConfig } = store.customerStore
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const navigate = useNavigate()

    const step = searchParams.get('section')

    const getTransaction = async () => {
        const { data } = await execute(() => getTransactionService({ globalTransactionNumber: id!, transactionType }))
        if(data?.status === TRANSACTION_STATUS.COMPLETED) await execute(()=> getLotDetails({globalTransactionNumber:data?.globalTransactionNumber!}))
        if (data !== null) {
            switch (data.status) {
                case TRANSACTION_STATUS.INBOUND_INITIATED:
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=INBOUND')
                    break;
                case TRANSACTION_STATUS.PHYSICAL_ORDER_INITIATED:
                    setCapturedSteps(['INBOUND'])
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=PHYSICAL_ORDER')
                    break;
                case TRANSACTION_STATUS.PVC_TO_PLANT_INITIATED:
                    setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER'])
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=PVC_TO_PLANT')
                    break;
                case TRANSACTION_STATUS.CONFIRM_PHYSICAL_ORDER:
                    setCapturedSteps(['INBOUND','PHYSICAL_ORDER','PVC_TO_PLANT'])
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=CONFIRM_PHYSICAL_ORDER')
                    break;
                case TRANSACTION_STATUS.PAYMENT_INITIATED:
                    setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER','PVC_TO_PLANT','CONFIRM_PHYSICAL_ORDER'])
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=BILLING')
                    break;
                case TRANSACTION_STATUS.DELIVERY_INITIATED :
                    setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER','PVC_TO_PLANT','CONFIRM_PHYSICAL_ORDER', 'BILLING'])
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=DELIVERY')
                    break;
                case TRANSACTION_STATUS.COMPLETED:
                case TRANSACTION_STATUS.CANCELLED:
                case TRANSACTION_STATUS.PENDING_CANCELLATION:
                    setCapturedSteps(['INBOUND', 'PHYSICAL_ORDER','PVC_TO_PLANT','CONFIRM_PHYSICAL_ORDER', 'BILLING', 'DELIVERY'])
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=SUMMARY')
                    break;
                default:
                    navigate(PATHS.JOB_WORK + '/' + id + '?section=INBOUND')
                    break;
            }
        }
    }

    const getSummary = () => {
        let totalWBM = 0
        let totalWAM = 0
        let totalWAMT = 0
        let settlementFineWeightGold = 0
        let totalMetalReturnNetWeightGold = 0
        console.log('in summary...', meltingLot)
        meltingLot.forEach(lot => {
            console.log('lot in summary...')
            totalWBM += lot.netWeight
            totalWAM += lot.weightAfterMelting
            totalWAMT += lot.wamt
            settlementFineWeightGold += lot.fineWeightToReturn
            totalMetalReturnNetWeightGold += lot.finalSettlementNetWeight
        })
        let totalTaxValue = 0
        physicalOrder.taxes?.forEach((tax) => {
            totalTaxValue += tax.taxAmount
        })
        let totalCharges = 0
        physicalOrder.charges?.forEach((charge) => {
            totalCharges += charge.preTaxAmount
            totalTaxValue += charge.postTaxAmount - charge.preTaxAmount
        })
        const roundingRs = getTwoPointNumber((Math.round(physicalOrder.totalPrice || 0) - (physicalOrder.totalPrice || 0)) ?? 0)
        return SUMMARY_DETAILS.map(field => {
            switch (field.id) {
                case 'customerId':
                    field.value = customerConfig.customerRefNo
                    break;
                case 'customerName':
                    field.value = customerConfig.fullName
                    break;
                case 'pvcName':
                    field.value = pvcCenterDetails.name
                    break;
                // case 'inboundMetal':
                //     field.value = physicalOrder.totalGoldWeight + ' gm'
                //     break;
                // case 'totalPrice':
                //     field.value = physicalOrder.totalPrice + ' gm'
                //     break;
                case 'transactionDate':
                    field.value = updatedAt ? formatDate(updatedAt) : ''
                    break;
                case 'deliveryCharge':
                    const deliveryCharge = physicalOrder.charges?.filter((charge)=>{
                        return charge.chargeId === CHARGE_TYPES.DELIVERY_CHARGE
                    })[0]
                    field.value = deliveryCharge ? getFormattedCurrency(deliveryCharge.postTaxAmount) : null
                    break;
                case 'totalWBMGold':
                    field.value = getThreePointNumber(totalWBM)
                    break;
                case 'totalWAMGold':
                    field.value = getThreePointNumber(totalWAM)
                    break;
                case 'totalWAMTGold':
                    field.value = getThreePointNumber(totalWAMT)
                    break;
                case 'settlementFineWeightGold':
                    field.value = getThreePointNumber(settlementFineWeightGold)
                    break;
                case 'totalMetalReturnNetWeightGold':
                    field.value = getThreePointNumber(totalMetalReturnNetWeightGold)
                    break;
                case 'totalTaxValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalTaxValue))
                    break;
                case 'totalCharges':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalCharges))
                    break;
                case 'roundingRs':
                    field.value = getFormattedCurrency(getTwoPointNumber(roundingRs))
                    break;
                case 'totalValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(physicalOrder.totalPrice || 0))
                    break;
            }
            return field
        })
    }

    const cancelTransactionHandler = (transactionId:string) => {
        navigate(PATHS.PVC_TRANSACTIONS)
    }

    useEffect(() => {
        setPageState(getPageInfo(step as TJobWorkSectionKeys))
    }, [step])

    useEffect(() => {
        resetOrder()

        if (id) {
            getTransaction()
        }
        else {

            setTransactionInfo({
                currency: 'INR',
                pvcCenterId: user.centerId!,
                status: TRANSACTION_STATUS.INBOUND_INITIATED,
                transactionType: 'JOB_WORK',
                weightUOM: 'GM',
            } as TTransactionBasic)
        }

        return () => resetOrder()

    }, [])

    const PageContent = useCallback(() => {
        switch (step as TJobWorkSectionKeys) {
            case 'INBOUND':
                return <Inbound path={PATHS.JOB_WORK} nextSection='PHYSICAL_ORDER' nextButtonLable='Next (Physical Order)' />

            case 'PHYSICAL_ORDER':
                return <PhysicalOrder path={PATHS.JOB_WORK} nextSection='PVC_TO_PLANT' nextButtonLable='Next (PVC To Plant)' prevSection='INBOUND' />

            case 'PVC_TO_PLANT':
                return <TransferPvcToPlant path={PATHS.JOB_WORK} nextSection='CONFIRM_PHYSICAL_ORDER' nextButtonLable='Next (Confirm Physical Order)' prevSection='PHYSICAL_ORDER' />

            case 'CONFIRM_PHYSICAL_ORDER':
                return <ConfirmPhysicalOrder path={PATHS.JOB_WORK} nextSection='BILLING' nextButtonLable='Next (Billing)' prevSection='PVC_TO_PLANT' />

            case 'BILLING':
                return <Billing path={PATHS.JOB_WORK} nextSection='DELIVERY' nextButtonLable='Next (Delivery)' prevSection='CONFIRM_PHYSICAL_ORDER' />

            case 'DELIVERY':
                return <Delivery path={PATHS.JOB_WORK} nextSection='SUMMARY' nextButtonLable='Generate Invoice' prevSection='BILLING' />

            case 'SUMMARY':
                return <Summary path={PATHS.JOB_WORK} prevSection='DELIVERY' transitionHistory={[]} summaryDetails={getSummary()} />

            default:
                return <SelectCustomer path={PATHS.JOB_WORK} nextSection='INBOUND' />
        }
    }, [step, inboundDetails])

    const DownloadAnnexure =async()=>{
        if (globalTransactionNumber) {
            const { data, error } = await execute(() => fetchAnnexure({ transactionType, globalTransactionNumber  }))
            console.log(data, "annexure docc");
            
            //@ts-ignore
            const uint8Array = new Uint8Array(data.data)
            // Create a Blob from the Uint8Array
            const blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            // Create a URL for the Blob
            const url = URL.createObjectURL(blob);
            // Create a link element and trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = `ANNEXURE_${globalTransactionNumber}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Revoke the object URL
            URL.revokeObjectURL(url);
        }
    }

    return (
        <Container>
            { id ? <HighlightsCardAndCustomerDetails
                        customerConfig={customerConfig}
                        globalTransactionNumber={globalTransactionNumber as string}
                        status={status}
                        inboundId={inboundDetails.id}
                        onSuccessCancelTransaction={cancelTransactionHandler}
                        transactionType={transactionType}
                    /> 
                : <></>
             }
            <PageContent />
            {
                [TRANSACTION_STATUS.PAYMENT_INITIATED, TRANSACTION_STATUS.DELIVERY_INITIATED, TRANSACTION_STATUS.COMPLETED].includes(status) && step != 'SUMMARY' ?
                    <div style={{padding:'10px'}}>
                        <DownloadInvoice
                            fileName={globalTransactionNumber!}
                            label='Download Delivery Challan'
                            documentType='DELIVERY_CHALLAN'
                        />
                        <Button size="small" onClick={DownloadAnnexure} variant="outlined" startIcon={<Description />}>
                        {'Download Annexure'}
                        </Button>
                    </div> :
                    <></>
            }
        </Container>
    )
}



export default JobWork